import { ReloadOutlined } from '@ant-design/icons';
import { Alert, Button, Grid, Switch, Table, notification } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useEffect, useState } from 'react';
import { useCurrentRoles } from 'hooks/use-current-roles';
import { extractArray, formatDateTime } from 'pages/star-shared/utils';
import UserAvatar from 'components/user-avatar';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import { useDataImport } from '../context';
import { ImportPermission } from '../types';
import { LOAD_FAILED_TEXT, normalizePermission } from '../utils';
import styles from '../style.module.scss';

const nameOf = (row: ImportPermission) =>
  row.nickName || row.username || row.email || `#${row.userId}`;

// Trang quản trị của người được cấp nhớ quyền theo phiên đăng nhập: menu "Nhập dữ liệu" chỉ
// hiện sau khi họ tải lại trang hoặc đăng nhập lại.
const RELOAD_HINT =
  'Người được cấp cần tải lại trang (F5) hoặc đăng nhập lại để thấy menu Nhập dữ liệu.';

/**
 * Tab "Người được nhập" (chỉ ADMIN): Admin chọn từng tài khoản MOD được nhập dữ liệu.
 * Mỗi lần bật / tắt được ghi vào Lịch sử nhập (Cấp quyền / Thu quyền).
 */
const PermissionsTab = () => {
  const { reportError } = useDataImport();
  const { displayName } = useCurrentRoles();
  const [rows, setRows] = useState<ImportPermission[]>([]);
  const [loading, setLoading] = useState(true);
  // Lần tải gần nhất bị lỗi: bảng ghi "chưa tải được" thay vì "chưa có tài khoản MOD nào".
  const [failed, setFailed] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  // Các tài khoản đang chờ máy chủ xác nhận.
  const [saving, setSaving] = useState<number[]>([]);
  // Màn hẹp (điện thoại): chỉ còn cột tài khoản + công tắc để công tắc luôn nằm trong màn
  // hình; người cấp / lúc cấp chuyển xuống dưới tên tài khoản.
  const compact = Grid.useBreakpoint().md === false;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    AxiosUtils.get(API.dataImportPermissions)
      .then((res: any) => {
        if (!cancelled) setRows(extractArray<any>(res).map(normalizePermission));
      })
      .catch((err) => {
        if (cancelled) return;
        // Giữ danh sách đang hiện (nếu có): tải lại lỗi không có nghĩa là hết tài khoản MOD.
        setFailed(true);
        reportError(err, 'Không tải được danh sách người được nhập');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey, reportError]);

  const replace = (userId: number, next: (row: ImportPermission) => ImportPermission) =>
    setRows((list) => list.map((row) => (row.userId === userId ? next(row) : row)));

  const toggle = async (before: ImportPermission, allowed: boolean) => {
    const { userId } = before;
    setSaving((ids) => [...ids, userId]);
    // Đổi ngay trên màn hình; máy chủ từ chối thì trả lại trạng thái cũ.
    replace(userId, (row) => ({ ...row, allowed }));
    try {
      const res: any = await AxiosUtils.put(`${API.dataImportPermissions}/${userId}`, { allowed });
      const saved = res?.data || {};
      replace(userId, (row) => ({
        ...row,
        allowed,
        grantedByName: saved.grantedByName ?? displayName ?? row.grantedByName,
        updatedAt: saved.updatedAt ?? saved.updated_at ?? new Date().toISOString(),
      }));
      notification.success({
        message: allowed
          ? `Đã cấp quyền nhập dữ liệu cho ${nameOf(before)}`
          : `Đã thu quyền nhập dữ liệu của ${nameOf(before)}`,
        description: allowed ? RELOAD_HINT : undefined,
        duration: allowed ? 8 : undefined,
      });
    } catch (err) {
      replace(userId, () => before);
      reportError(err, 'Không đổi được quyền nhập');
    } finally {
      setSaving((ids) => ids.filter((id) => id !== userId));
    }
  };

  const columns: ColumnsType<ImportPermission> = [
    {
      title: 'Tài khoản MOD',
      key: 'user',
      render: (_, row) => (
        <div className={styles.person}>
          {/* Màn hẹp: bỏ ảnh đại diện, email xuống dòng riêng để không bị bẻ giữa chừng. */}
          {!compact && (
            <UserAvatar src={row.avatar} />
          )}
          <div>
            <div className={styles.personName}>{nameOf(row)}</div>
            <div className={styles.personSub}>
              #{row.userId}
              {row.username ? ` · @${row.username}` : ''}
              {!compact && row.email ? ` · ${row.email}` : ''}
            </div>
            {compact && row.email && <div className={styles.personSub}>{row.email}</div>}
            {compact && (row.grantedByName || row.updatedAt) && (
              <div className={styles.personSub}>
                {row.grantedByName
                  ? `${row.allowed ? 'Người cấp' : 'Người thu'}: ${row.grantedByName}`
                  : 'Cập nhật'}
                {row.updatedAt ? ` · ${formatDateTime(row.updatedAt)}` : ''}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: compact ? 'Được nhập' : 'Được nhập dữ liệu',
      key: 'allowed',
      width: compact ? 132 : 200,
      // Chữ trạng thái đặt cạnh công tắc (chữ trắng trong công tắc xám không đủ tương phản).
      render: (_, row) => (
        <div className={styles.switchCell}>
          <Switch
            checked={row.allowed}
            loading={saving.includes(row.userId)}
            aria-label={`Quyền nhập dữ liệu của ${nameOf(row)}`}
            onChange={(checked) => toggle(row, checked)}
          />
          <span className={row.allowed ? styles.switchOn : styles.switchOff}>
            {row.allowed ? 'Được nhập' : row.grantedByName ? 'Đã thu quyền' : 'Chưa cấp'}
          </span>
        </div>
      ),
    },
  ];

  if (!compact) {
    columns.push(
      {
        // Máy chủ lưu người đổi gần nhất: người cấp khi đang bật, người thu khi đã tắt.
        title: 'Người cấp / thu',
        dataIndex: 'grantedByName',
        width: 200,
        render: (value: string | null) => value || '—',
      },
      {
        title: 'Cập nhật lúc',
        dataIndex: 'updatedAt',
        width: 180,
        render: (value: string | null) => formatDateTime(value),
      }
    );
  }

  return (
    <div>
      <Alert
        className={styles.section}
        type="info"
        showIcon
        message="Admin luôn có quyền nhập"
        description={`Tài khoản MOD chỉ nhập được file và sửa tay khi được bật ở đây. ${RELOAD_HINT} Tắt công tắc là thu quyền ngay; số liệu đã nhập vẫn giữ nguyên.`}
      />
      {failed && rows.length > 0 && (
        <Alert
          className={styles.section}
          type="warning"
          showIcon
          message="Chưa tải lại được. Danh sách đang hiện là của lần tải trước."
        />
      )}
      <div className={styles.toolbar}>
        <Button
          icon={<ReloadOutlined />}
          loading={loading}
          onClick={() => setRefreshKey((key) => key + 1)}
        >
          Tải lại
        </Button>
      </div>
      <div className="custom-table">
        <Table
          rowKey="userId"
          loading={loading}
          dataSource={rows}
          columns={columns}
          scroll={compact ? undefined : { x: 860 }}
          pagination={{ pageSize: 20, hideOnSinglePage: true, showSizeChanger: false }}
          locale={{
            emptyText: failed ? LOAD_FAILED_TEXT : 'Chưa có tài khoản MOD nào đang hoạt động.',
          }}
        />
      </div>
    </div>
  );
};

export default PermissionsTab;
